import { ExpandIcon } from "@hugeicons/core-free-icons"
import { HugeiconsIcon } from "@hugeicons/react"
import type { ChangeEventHandler, ComponentProps, Ref } from "react"
import { cn } from "../../lib"
import {
  Field,
  FieldLabel,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Textarea,
} from "../ui"
import FieldDescriptionOrError from "./FieldDescriptionOrError"

type Props = Pick<
  ComponentProps<typeof InputGroupInput | typeof Textarea>,
  | "id"
  | "value"
  | "name"
  | "required"
  | "className"
  | "placeholder"
  | "disabled"
> & {
  ref?: Ref<HTMLInputElement & HTMLTextAreaElement>
  onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
  label?: string
  error?: string | null
  helperText?: string
}

export default function ExpandableInputField({
  label,
  error,
  helperText,
  name,
  required,
  className,
  placeholder,
  ...props
}: Props) {
  return (
    <Field className={className} data-invalid={!!error}>
      {label && (
        <FieldLabel
          htmlFor={`${name}-input`}
          className={cn("text-nowrap", required && "after:content-['*']")}
        >
          {label}
        </FieldLabel>
      )}
      <InputGroup>
        <InputGroupInput
          id={`${name}-input`}
          name={name}
          placeholder={
            required && placeholder ? `${placeholder} *` : placeholder
          }
          aria-invalid={!!error}
          {...props}
        />
        <Popover>
          <PopoverTrigger
            nativeButton={false}
            render={
              <InputGroupAddon className="cursor-pointer" align="inline-end">
                <HugeiconsIcon icon={ExpandIcon} />
              </InputGroupAddon>
            }
          />
          <PopoverContent side="inline-start" className="mb-4 w-96 p-4">
            {label && (
              <Label
                htmlFor={`${name}-textarea`}
                className={cn(required && "after:content-['*']")}
              >
                {label}
              </Label>
            )}
            <Textarea
              id={`${name}-textarea`}
              name={name}
              placeholder={
                required && placeholder ? `${placeholder} *` : placeholder
              }
              aria-invalid={!!error}
              {...props}
              autoResize
            />
          </PopoverContent>
        </Popover>
      </InputGroup>
      <FieldDescriptionOrError description={helperText} error={error} />
    </Field>
  )
}
